// Which season sells each night of the contract validity (PRD US-019). Pure,
// no IO: a port of the API's src/modules/hotels/seasons/SeasonCoverage.ts
// (rules FR-V3 / FR-V4). Both sides run __fixtures__/coverage.json; keep the
// two in sync (copy the fixture from the API when it changes).
import NightCoverageEnum from 'constants/enums/NightCoverageEnum';
import { CLOSED } from './seasonModel';

const CLUSTER_DAYS = 7;
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_MS = 86_400_000;

const toMs = (iso) => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
const toIso = (ms) => new Date(ms).toISOString().slice(0, 10);
const formatDate = (iso) =>
  `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const pluralDays = (n) => `${n} day${n > 1 ? 's' : ''}`;

/** ISO weekday of a yyyy-MM-dd date: 1 = Monday … 7 = Sunday. */
export const isoWeekday = (iso) => new Date(toMs(iso)).getUTCDay() || 7;

const coversNight = (season, period, date) =>
  Boolean(period.startAt && period.endAt) &&
  date >= period.startAt.slice(0, 10) &&
  date <= period.endAt.slice(0, 10) &&
  season.daysOfWeek.includes(isoWeekday(date));

export const nightState = (date, seasons) => {
  const hits = seasons.filter((season) =>
    season.periods.some((period) => coversNight(season, period, date)),
  );
  const seasonIds = hits.map((season) => season.id);
  if (hits.length === 0) {
    return { date, state: NightCoverageEnum.GAP, seasonIds };
  }
  if (hits.length > 1) {
    return { date, state: NightCoverageEnum.DOUBLE, seasonIds };
  }
  const state =
    hits[0].category === CLOSED
      ? NightCoverageEnum.CLOSED
      : NightCoverageEnum.COVERED;
  return { date, state, seasonIds };
};

const cluster = (nights, kind) => {
  const groups = [];
  for (const night of nights.filter((n) => n.state === kind)) {
    const group = groups[groups.length - 1];
    const last = group?.[group.length - 1];
    if (last && toMs(night.date) - toMs(last.date) <= CLUSTER_DAYS * DAY_MS) {
      group.push(night);
    } else {
      groups.push([night]);
    }
  }

  return groups.map((group) => {
    const startAt = group[0].date;
    const endAt = group[group.length - 1].date;
    const span = (toMs(endAt) - toMs(startAt)) / DAY_MS + 1;
    const partial = group.length < span;
    const weekdays = [...new Set(group.map((n) => isoWeekday(n.date)))].sort();
    const seasonIds = [...new Set(group.flatMap((n) => n.seasonIds))];
    const range = `${formatDate(startAt)} → ${formatDate(endAt)} · ${pluralDays(
      group.length,
    )}`;
    const days = partial
      ? ` (${weekdays.map((d) => WEEKDAYS[d - 1]).join(', ')})`
      : '';
    const seasonsPerNight = Math.max(...group.map((n) => n.seasonIds.length));
    return {
      kind,
      startAt,
      endAt,
      nights: group.length,
      daysOfWeek: partial ? weekdays : [1, 2, 3, 4, 5, 6, 7],
      partial,
      seasonIds: kind === NightCoverageEnum.DOUBLE ? seasonIds : [],
      message:
        kind === NightCoverageEnum.GAP
          ? `${range} not covered by any season${days}`
          : `${range} in ${seasonsPerNight} seasons${days} · adjust dates or days`,
    };
  });
};

/**
 * Per-night state (NightCoverageEnum) over the validity
 * (inclusive, yyyy-MM-dd), the grouped gap / double-booking issues, and the
 * "All N days covered" summary when there is no issue (else null).
 */
export const computeCoverage = (validity, seasons) => {
  const nights = [];
  for (
    let ms = toMs(validity.startAt);
    ms <= toMs(validity.endAt);
    ms += DAY_MS
  ) {
    nights.push(nightState(toIso(ms), seasons));
  }

  const gaps = cluster(nights, NightCoverageEnum.GAP);
  const doubles = cluster(nights, NightCoverageEnum.DOUBLE);
  return {
    nights,
    gaps,
    doubles,
    summary:
      gaps.length === 0 && doubles.length === 0
        ? `All ${nights.length} days covered (incl. closed) · no gap, no double booking`
        : null,
  };
};
